import { faWarning } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { alpha, Box, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getActiveAppContext } from "app/tools/common"
import { MAP_GEO_TARGETS_CONTAINER_ID } from "form/map/model"
import { createGeoTargetsMap, destroyGeoTargetsMap } from "form/map/tools/geo-targets"
import { centerMapOnCoordinateFeature } from "form/map/tools/geo-targets-coordinate"
import { centerMapOnShape } from "form/map/tools/geo-targets-shape"
import {
  GEO_TARGETS_FORM_TABS,
  GEO_TARGETS_FORM_WIDTH,
  GEO_TARGETS_MAP_HEIGHT,
  GEO_TARGETS_TYPES,
  IForm,
  IFormCoordinate
} from "form/model"
import { getForm } from "form/tools/common"
import { activateGeoTargetsTab, getGeoTargetsForm, startUpdatingGeoTargetsForm } from "form/tools/geo-targets"
import { messageSVC } from "message/service"
import React from "react"
import { connect } from "react-redux"
import { IGeoShape } from "shape/model"
import { FlexBox } from "ui/components/FlexBox"
import { GeoTargetsForm } from "./geo-targets/GeoTargetsForm"
import { GeoTargetsList } from "./geo-targets/GeoTargetsList"

interface IContentGeoTargetsProps {
  form: IForm | null
  hasNoIntersectionError: boolean
  isTightContent: boolean
}

const ContentGeoTargetsFn = React.memo(({ form, hasNoIntersectionError, isTightContent }: IContentGeoTargetsProps) => {
  if (form === null) {
    return null
  }
  React.useEffect(() => {
    createGeoTargetsMap(MAP_GEO_TARGETS_CONTAINER_ID)
    return () => destroyGeoTargetsMap()
  }, [])
  return (
    <FlexBox
      vertical
      ai="start"
      jc="start"
      fullWidth
      sx={{
        "height": "100%",
        "mt": 0,
        "& > *": {
          width: "100%"
        }
      }}>
      <FlexBox wrap gap sx={{ height: isTightContent ? undefined : GEO_TARGETS_MAP_HEIGHT }}>
        <GeoTargetsForm />
        <Box
          id={MAP_GEO_TARGETS_CONTAINER_ID}
          sx={{
            "position": "relative",
            "width": isTightContent ? "100%" : `calc(100% - ${GEO_TARGETS_FORM_WIDTH} - 1rem)`,
            "height": isTightContent ? undefined : GEO_TARGETS_MAP_HEIGHT,
            "minHeight": isTightContent ? "15rem" : undefined,
            "borderRadius": ".25rem",
            "& .mapbox-improve-map, & .mapboxgl-ctrl-logo": { display: "none" }
          }}>
          {hasNoIntersectionError && (
            <FlexBox
              fullWidth
              sx={{
                position: "absolute",
                top: 0,
                zIndex: 1,
                p: "1rem"
              }}>
              <FlexBox
                gap
                sx={{
                  "p": "1rem",
                  "borderRadius": ".125rem",
                  "backgroundColor": theme => alpha(theme.palette.error.main, 0.7),
                  "& .icon": { color: theme => theme.palette.error.contrastText }
                }}>
                <FontAwesomeIcon icon={faWarning} className="icon" size="2x" />
                <Typography
                  id={`${MAP_GEO_TARGETS_CONTAINER_ID}-intersection-error`}
                  fontSize=".85rem"
                  fontWeight="bold"
                  sx={{ color: theme => theme.palette.error.contrastText }}>
                  {translate("campaign.geo-target.no.intersection.error")}
                </Typography>
              </FlexBox>
            </FlexBox>
          )}
        </Box>
      </FlexBox>
      <FlexBox sx={{ mt: "1rem" }}>
        <GeoTargetsList
          form={form}
          onItemClick={item => {
            if (item.type === GEO_TARGETS_TYPES.COORDINATE) {
              centerMapOnCoordinateFeature(item.coordinate?.id as number)
              startUpdatingGeoTargetsForm(item.coordinate as IFormCoordinate)
            } else {
              if (getGeoTargetsForm().activeTab !== GEO_TARGETS_FORM_TABS.SHAPE) {
                activateGeoTargetsTab(GEO_TARGETS_FORM_TABS.SHAPE)
              }
              if (item.shape && item.shape.shape) {
                centerMapOnShape(item.shape as IGeoShape)
              } else {
                messageSVC.warning(translate("campaign.geo-targets.shape.missing"))
              }
            }
          }}
        />
      </FlexBox>
    </FlexBox>
  )
})

export const ContentGeoTargets = connect(
  (state: IRootState): IContentGeoTargetsProps => ({
    form: getActiveAppContext(state) ? getForm(state) : null,
    hasNoIntersectionError: getActiveAppContext(state) ? getGeoTargetsForm(state).noIntersectionError : false,
    isTightContent: state.ui.contentWidth < 760
  })
)(ContentGeoTargetsFn)
